/**
 * The CRF feature's wire layer — Figma page 03 (v7 Block A), six screens on one
 * contract.
 *
 * Every DTO below is TRANSCRIBED from `apps/api/src/modules/crfs/crfs.service.ts`
 * (`CrfView`, the register row) and `crf-files.service.ts` (`CrfFileView`), and
 * carries only the fields a screen renders — a field this module claims to know
 * but never reads is a field that will be wrong the first time somebody trusts
 * it (the `PartyDto` rule).
 *
 * MONEY: `amount` and `advance_amount` arrive EITHER as the figure OR as `•••`
 * depending on the caller's `amounts.view` — the server decides, this module
 * never does. Render through the masked-cell idiom; never parseFloat a value
 * that might be a mask.
 *
 * Every call goes through `apiRequest` (single-flight refresh mutex, D-web-1).
 */

import { apiRequest, fetchBinary, type BinaryResponse } from '../../lib/api';
import type { PaymentStatus } from '../work-pipeline/api';
import type { ManualDocType, WorkType } from './domain';

/* ─────────────────────────────────────────────────────────────────── DTOs ── */

/**
 * One attached value from the SHARED PLATFORM VOCABULARY (migration 38, owner
 * ruling Q8). Succeeds `CrfModuleDto`, whose `module_key` this module used to
 * translate against a hand copy of the server's four labels.
 *
 * ⚠ `name` COMES FROM THE SERVER. That is the ruling in one field: the words are
 * the vocabulary's, the vocabulary is a table the owner edits, and nothing here
 * looks a label up. A value the owner has retired still arrives with its name, so
 * a record that carries it still reads correctly.
 */
export interface CrfPlatformDto {
  platform_id: string;
  name: string;
  detail: string | null;
}

/**
 * One offerable value for the Customization picker — what may be NEWLY ticked.
 *
 * ⚠ `applies_to` IS A LIST, NOT A WORD — the API's `PlatformOptionView`, mirrored. It
 * became one when migration 48 reshaped `platforms.applies_to` into a SET under owner
 * ruling 5 (*"a row may carry any combination"*), and the pre-48 `'both'` no longer
 * exists as a value anywhere.
 *
 * ⚠ NOTHING ON THIS SCREEN BRANCHES ON IT, AND THAT IS DELIBERATE. The server has
 * already excluded every value this work type cannot carry (see below), so the field is
 * a description of the row rather than an instruction to the client. It is typed as a
 * list rather than as the column's raw `'crf,project'` text precisely so that a future
 * reader who DOES branch on it cannot reach for `applies_to === 'project'`, which is
 * silently false for every widened row.
 */
export interface PlatformOptionDto {
  id: string;
  name: string;
  sort_order: number;
  applies_to: string[];
}

export interface CrfPointDto {
  point_no: number;
  description: string;
  confirmation: 'pending' | 'confirmed' | 'not_confirmed' | 'on_hold';
  remark: string | null;
}

export interface CrfWorkOrderDto {
  id: string;
  type: string;
  status: string;
  assigned_to: string | null;
}

/** One attached stack (migration 39) — projects only. `stack_id`, matching the server. */
export interface CrfStackDto {
  stack_id: string;
  name: string;
}

export interface CrfDto {
  id: string;
  doc_no: string;
  /**
   * WHICH KIND OF WORK THIS RECORD IS — the Projects discriminator (migration 37).
   *
   * ⚠ NOT OPTIONAL AND NOT NULLABLE. `crfs.work_type` is `NOT NULL DEFAULT 'crf'`, so every
   * row that has ever existed carries it and the server sends it on every read. Typing it
   * `?` would have let a screen quietly render the CRF branch for a project because a field
   * was absent — the failure mode the whole discriminator exists to prevent.
   */
  work_type: WorkType;
  country_id: number;
  party_id: string;
  inquiry_id: string | null;
  quote_id: string | null;
  title: string;
  status: string;
  fy_label: string;
  /** Money — the figure or '•••', the server's call. */
  amount: string | null;
  currency: string | null;
  /** Money (migration 20) — same masking contract as `amount`. */
  advance_amount: string | null;
  /**
   * Money (migration 64, v24 Phase B) — DERIVED server-side: `amount − received`
   * over the CRF's payment ledger. Same masking contract as `amount`. NULL is
   * PB-4's UNRESOLVED state (no established amount, so no arithmetic) and renders
   * as the em-dash — never as 0.00: zero pending is earned, not defaulted.
   */
  pending_amount: string | null;
  /**
   * v22-E3 — the country's payment split, DERIVED server-side from `amount`
   * and SHOWN, never tracked. ABSENT (not null) when the country has no split
   * or the CRF has no amount — a no-split country's records are byte-for-byte
   * what they were before the feature. The two `_amount` members arrive
   * masked (`'•••'`) exactly as `amount` does; the percentages are country
   * configuration and stay readable.
   */
  payment_split?: {
    advance_pct: string;
    balance_pct: string;
    advance_amount: string;
    balance_amount: string;
    balance_trigger: 'on_delivery';
    delivered_at: string | null;
  };
  /**
   * The four-member ENUM of `crfs.payment_status` (migration 6) — the same closed set the
   * write route validates against (`@IsIn(PAYMENT_STATUSES)`, lifecycle.dto.ts) and the
   * work-order record already types this column as (`WorkOrderRecordDto.crf`). Narrowed from
   * `string` when the payment card was mounted on the CRF record (Train E, fork 15): the card
   * is `PaymentPanel`, whose label map is `Record<PaymentStatus, string>` on purpose, and a
   * `string` here would have forced either a cast at the mount or a lookup that silently
   * renders nothing. NOT money — never masked.
   */
  payment_status: PaymentStatus | null;
  legacy_ref: string | null;
  /**
   * HOW THIS JOB CAME IN — `crfs.referral` (migration 49), the per-document statement
   * beside the party's durable one (`PartyDto.referral`, the HOME of the fact). Free text
   * or NULL. On BOTH server converters (`toView` and `rowToListView`), so the register row
   * and the record carry it alike.
   *
   * ⚠ NOT MONEY, AND IT MUST STAY OUT OF THE MASKED-CELL IDIOM: it never arrives as `•••`
   * (`money-field-registry.spec.ts` records it in the NOT-MONEY table). A Developer whose
   * `amount` is masked still reads the referrer's name on the same response.
   *
   * OPTIONAL ON THE TYPE for the same reason `PartyDto.customer_country` is — the record
   * and list fixtures that predate the column still satisfy the mirror, and a missing key
   * renders exactly as `null` does ("Not recorded").
   *
   * WRITTEN three ways: at create (`CreateCrfBody.referral`, omitted = inherit the
   * party's), on the draft PATCH (`UpdateCrfBody.referral`, null = cleared), and on ANY
   * status through {@link setCrfReferral} — the E22 door the record's Customer group uses.
   */
  referral?: string | null;
  created_at: string;
  updated_at?: string;
  confirmation_sent_at: string | null;
  confirmation_reminders: number;
  /** List/record enrichment — present where the server joined them. */
  party_name?: string;
  platforms?: CrfPlatformDto[];
  /**
   * ⚠ PRESENT ONLY ON A PROJECT, and the ABSENCE is a statement rather than a gap: the
   * server omits the key entirely on a CRF (`crfs.service.ts` sets it only when
   * `work_type === 'project'`) because "this record has no stack yet" and "the question
   * does not arise for this record" are different facts. An empty array would collapse
   * them, and the record screen would offer a CRF a picker it may not use.
   */
  stacks?: CrfStackDto[];
  developer?: { id: string; name: string } | null;
  points?: CrfPointDto[];
  work_orders?: CrfWorkOrderDto[];
  /**
   * A4's tab badges. `files`/`test_rounds`/`deployments`/`manuals` — a 0 is a count,
   * not a gap.
   *
   * ⚠ `manuals` is a SUBSET of `files`, not a sibling: a manual IS a CRF attachment
   * that carries a `doc_type` (migration 55). A record with one Final Manual and
   * nothing else reads `files: 1, manuals: 1`, and the two badges are not meant to
   * sum to anything.
   */
  counts?: { test_rounds: number; deployments: number; files: number; manuals: number };
}

export interface Paged<T> {
  data: T[];
  page: number;
  perPage: number;
  total: number;
}

/** The A5 register row (`awaitingConfirmation` — transcribed, not guessed). */
export interface AwaitingConfirmationRowDto {
  id: string;
  doc_no: string;
  title: string;
  party_id: string;
  party_name: string;
  points_total: number;
  points_confirmed: number;
  developer: { id: string; name: string } | null;
  confirmation_sent_at: string;
  confirmation_reminders: number;
}

export interface CrfFileDto {
  id: string;
  crf_id: string;
  file_name: string;
  mime: string | null;
  size_bytes: number;
  created_at: string;
  /**
   * Migration 55 — which legacy document this is, or `null` for an ordinary
   * attachment. The manuals register is exactly the rows where this is not null.
   */
  doc_type: ManualDocType | null;
}

/* ─────────────────────────────────────────────────────────────────── reads ── */

export interface CrfListFilters {
  status?: string;
  q?: string;
  party?: string;
  /**
   * `crf` · `project` · `all` — the register's segment, answered by the SERVER.
   *
   * ⚠ ABSENT MEANS `crf`, WHICH IS THE SERVER'S DEFAULT AND NOT THIS CLIENT'S (design
   * §5.2a): the screen was the CRF register before migration 37 and its users' universe
   * must not widen under them without an act. So a caller that omits this keeps exactly
   * the rows it had, and "All" is a thing somebody chooses.
   *
   * ⚠ AND IT IS SERVER-SIDE FOR THE REASON THIS SCREEN'S OWN HEADER ALREADY GIVES ABOUT
   * THE STAGE SEGMENTS: the register is paged, so a segment applied to one fetched page
   * would show the projects on page 1 and call that the project register — "a quiet lie".
   */
  work_type?: string;
  /** Comma-separated `platforms.id`. OR within the axis, AND across the axes. */
  platform?: string;
  /** Comma-separated `stacks.id`. */
  stack?: string;
  /**
   * AM-1's one visible toggle (v24 A1.4). '1' shows TERMINAL legacy rows; ABSENT
   * is the server's default (browse hides them, a `q` search reaches everything)
   * — and absent means absent, for the same reason `work_type` above never
   * spells `crf`: the default lives in ONE place, the server.
   */
  include_history?: string;
  page?: number;
  perPage?: number;
}

export async function fetchCrfs(
  countryCode: string,
  filters: CrfListFilters = {},
  signal?: AbortSignal,
): Promise<Paged<CrfDto>> {
  return apiRequest<Paged<CrfDto>>(`/countries/${countryCode}/crfs`, {
    query: {
      status: filters.status || undefined,
      q: filters.q || undefined,
      party: filters.party || undefined,
      work_type: filters.work_type || undefined,
      platform: filters.platform || undefined,
      stack: filters.stack || undefined,
      include_history: filters.include_history || undefined,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

/**
 * Every enum stage, zero-filled — a 0 renders as 0, never as an absent card.
 *
 * ⚠ IT TAKES THE LIST'S FILTERS, and the API refuses to answer without them for the reason
 * it records: a KPI strip describing a different set of rows from the ones underneath it is
 * the most expensive kind of wrong answer on a dashboard, because it is right often enough
 * to be trusted.
 */
export async function fetchCrfStatusCounts(
  countryCode: string,
  filters: CrfCountFilters = {},
  signal?: AbortSignal,
): Promise<Record<string, number>> {
  return apiRequest<Record<string, number>>(`/countries/${countryCode}/crfs/status-counts`, {
    query: {
      work_type: filters.work_type || undefined,
      platform: filters.platform || undefined,
      stack: filters.stack || undefined,
      include_history: filters.include_history || undefined,
    },
    signal,
  });
}

/** What the two count routes may be narrowed by — the list's filters, minus paging. */
export interface CrfCountFilters {
  work_type?: string;
  platform?: string;
  stack?: string;
  /** AM-1 (A1.4): the strip takes the list's toggle — one register, one set. */
  include_history?: string;
}

/** One bucket — `VocabularyCount`, crfs.service.ts. The NAME travels with the count. */
export interface VocabularyCountDto {
  id: string;
  name: string;
  count: number;
}

export interface VocabularyCountsDto {
  platforms: VocabularyCountDto[];
  stacks: VocabularyCountDto[];
}

/**
 * "How much iOS work" and "how many Flutter projects" — SERVER aggregates over the whole
 * filtered set (design §5.2).
 *
 * ⚠ A SEPARATE QUERY FROM {@link fetchCrfStatusCounts}, DELIBERATELY. They could have been
 * one payload; two means a failed vocabulary count renders ABSENT without taking the status
 * strip down with it, which is this screen's KPI rule and not a nicety.
 *
 * ⚠ THE BUCKETS LEGITIMATELY SUM TO MORE THAN THE REGISTER'S TOTAL — a job that ships to
 * iOS and Android is counted under both. That is a property of the data, so the SCREEN says
 * it in words rather than anybody later filing the arithmetic as a defect.
 */
export async function fetchCrfVocabularyCounts(
  countryCode: string,
  filters: CrfCountFilters = {},
  signal?: AbortSignal,
): Promise<VocabularyCountsDto> {
  return apiRequest<VocabularyCountsDto>(`/countries/${countryCode}/crfs/vocabulary-counts`, {
    query: {
      work_type: filters.work_type || undefined,
      platform: filters.platform || undefined,
      stack: filters.stack || undefined,
      include_history: filters.include_history || undefined,
    },
    signal,
  });
}

export async function fetchAwaitingConfirmation(
  countryCode: string,
  paging: { page?: number; perPage?: number } = {},
  signal?: AbortSignal,
): Promise<Paged<AwaitingConfirmationRowDto>> {
  return apiRequest<Paged<AwaitingConfirmationRowDto>>(
    `/countries/${countryCode}/crfs/awaiting-confirmation`,
    { query: { page: paging.page, perPage: paging.perPage }, signal },
  );
}

export async function fetchCrf(crfId: string, signal?: AbortSignal): Promise<CrfDto> {
  return apiRequest<CrfDto>(`/crfs/${crfId}`, { signal });
}

/**
 * The Customization picker's options — what THIS country may newly attach today.
 *
 * ⚠ THE SERVER DOES THE FILTERING, and this function's whole existence is the Q8
 * ruling: retired values, values this country has switched off, and values whose
 * `applies_to` does not admit a CRF are already absent from the response. The
 * client renders what it is given and holds no vocabulary of its own.
 *
 * This is the AFFORDANCE, not the guard. The server refuses a bad `platform_id`
 * on write regardless of what was offered here — a refusal the server does not
 * make is not a guard, and a picker is not a refusal.
 */
export async function fetchCrfPlatformOptions(
  countryCode: string,
  workType?: WorkType,
  signal?: AbortSignal,
): Promise<PlatformOptionDto[]> {
  return apiRequest<PlatformOptionDto[]>(`/countries/${countryCode}/crfs/platforms`, {
    // Absent means `crf` on the SERVER, so every caller that existed before migration 37
    // keeps the answer it already had. A project screen asks for its own applicability.
    query: { work_type: workType },
    signal,
  });
}

/** One offerable stack — group-wide, no `applies_to` and no per-country flag (D-2b). */
export interface StackOptionDto {
  id: string;
  name: string;
  sort_order: number;
}

/**
 * The project stack picker (migration 39).
 *
 * ⚠ NOT `/countries/:cc/stacks`, AND THAT ROUTE MUST NEVER EXIST (D-2b): a country does not
 * choose what our engineers build in. This is a CRF-surface question about a group-wide
 * list, which is exactly the path it sits on.
 */
export async function fetchCrfStackOptions(
  countryCode: string,
  signal?: AbortSignal,
): Promise<StackOptionDto[]> {
  return apiRequest<StackOptionDto[]>(`/countries/${countryCode}/crfs/stacks`, { signal });
}

/* ────────────────────────────────────────────────────────────────── writes ── */

export interface CrfPlatformEntry {
  platform_id: string;
  detail?: string | null;
}

export interface CreateCrfBody {
  party_id: string;
  title: string;
  inquiry_id?: string | null;
  quote_id?: string | null;
  amount?: string | null;
  currency?: string | null;
  advance_amount?: string | null;
  legacy_ref?: string | null;
  /**
   * `CreateCrfDto.referral` — how THIS job came in, as said on the day (migration 49;
   * owner ruling R4 *"capture them, never require them"*). OPTIONAL — the ruling. Free
   * text, at most 200 characters.
   *
   * ⚠ ABSENT / null / BLANK MEANS "INHERIT THE PARTY'S", NOT "NONE" — the SERVER decides
   * (`CrfsService.create`: `normaliseReferral(dto.referral) ?? party.referral`). A blank
   * is no statement, and a tabbed-past field does not unsay what the customer record
   * already says; NULL lands on the row at create only when the party's is NULL too. So
   * `CrfCreatePage` OMITS the key when its field is blank, and a draft that wants "none"
   * afterwards says so on the PATCH (`UpdateCrfBody.referral: null`).
   */
  referral?: string | null;
  platforms?: CrfPlatformEntry[];
}

export async function createCrf(countryCode: string, body: CreateCrfBody): Promise<CrfDto> {
  return apiRequest<CrfDto>(`/countries/${countryCode}/crfs`, { method: 'POST', body });
}

/**
 * The Save Draft round-trip. Draft-only on the server (409 otherwise);
 * `platforms: undefined` = untouched, `platforms: []` = cleared — two statements,
 * both expressible, and callers must pick deliberately.
 *
 * `referral` makes THREE statements on the PATCH, the same three `platforms` does:
 * `undefined` = untouched, `null` (or blank — the server trims) = CLEARED to NULL, a
 * string = replaced. NO inherit on a PATCH — the draft already exists and a clear is a
 * clear (`UpdateCrfDto.referral`, crfs.dto.ts).
 */
export type UpdateCrfBody = Partial<Omit<CreateCrfBody, 'party_id'>>;

export async function updateCrf(crfId: string, body: UpdateCrfBody): Promise<CrfDto> {
  return apiRequest<CrfDto>(`/crfs/${crfId}`, { method: 'PATCH', body });
}

/**
 * `POST /crfs/:id/referral` — THE REFERRAL DOOR ON A CRF OF ANY STATUS (E22).
 *
 * The draft PATCH refuses a confirmed record (409), and the owner ruled that a commercial
 * fact learned AFTER confirmation is still recordable rather than a spreadsheet's job. So
 * this is its own route: `crf.edit`, country-scoped, NO status gate, one column, its own
 * verb (`crf.referral_set`, payload `{doc_no}` — the referrer's name stays off the feed).
 * It returns the whole record, so the caller can put the response straight into the cache.
 *
 * ⚠ THE KEY IS MANDATORY ON THE WIRE. `{}` is a 400 naming the field, NOT a no-op and NOT a
 * clear (`SetCrfReferralDto` uses `@ValidateIf(v !== null)`, not `@IsOptional()`). Callers
 * send `{ referral: null }` to CLEAR and `{ referral: 'Shah & Co' }` to SET; the server
 * trims and turns blank into NULL exactly as the create path does — one rule,
 * `parties/referral.ts` — and otherwise stores what was typed. It re-cases nothing: the
 * `direct` → `Direct` fold this line used to name was struck by the owner on 2026-08-18.
 * NO inherit here: inherit belongs to CREATE.
 */
export interface SetCrfReferralBody {
  referral: string | null;
}

export async function setCrfReferral(crfId: string, body: SetCrfReferralBody): Promise<CrfDto> {
  return apiRequest<CrfDto>(`/crfs/${crfId}/referral`, { method: 'POST', body });
}

/**
 * `POST /crfs/:id/advance` — THE ADVANCE DOOR, FILL-A-BLANK ONLY (Train E · E26, owner
 * fork 14: *"an advance learned AFTER confirmation IS recordable: a named single-column
 * route that REFUSES TO OVERWRITE. Frozen, the workaround is a spreadsheet."*).
 *
 * The referral door's shape — `crf.edit`, country-scoped, NO status gate, one column, its
 * own verb (`crf.advance_recorded`, payload `{doc_no}` — the figure stays off the feed) —
 * with the ONE rule that door does not have: a recorded advance is refused 409, never
 * overwritten and never cleared. `advance_amount` is REQUIRED and never `null` on this
 * wire (`null` is a 400 at the server's pipe: a clear is an overwrite by another name),
 * which is why this body is `string`, not `string | null` like the referral's.
 *
 * MONEY IN: the figure goes as the operator typed it, a MONEY_14_2 decimal literal in the
 * record's own currency (the route takes NO currency — the record has exactly one). An
 * advance against an unpriced record is a 400 with the service's existing sentence. MONEY
 * OUT: the response is the WHOLE record through the masking interceptor, so the caller
 * puts it straight into the record cache and the figure — or `'•••'`, the server's call —
 * lands on screen the moment the form closes.
 */
export interface RecordCrfAdvanceBody {
  advance_amount: string;
}

export async function recordCrfAdvance(
  crfId: string,
  body: RecordCrfAdvanceBody,
): Promise<CrfDto> {
  return apiRequest<CrfDto>(`/crfs/${crfId}/advance`, { method: 'POST', body });
}

/* ── the payments ledger (migration 64, v24 Phase B) ───────────────────────── */

/**
 * One immutable ledger row (RA-4: no update, no delete — corrections are new
 * rows). `amount` arrives masked ('•••') for seats without `amounts.view`,
 * exactly like the CRF's own figures; `kind` and `currency` are vocabulary and
 * stay readable.
 */
export interface PaymentEntryDto {
  id: string;
  kind: 'payment' | 'refund' | 'adjustment' | 'opening_balance';
  amount: string;
  currency: string;
  paid_on: string;
  method: 'bank' | 'cash' | 'cheque' | 'upi' | 'card' | 'other' | null;
  invoice_ref: string | null;
  source: 'manual' | 'legacy';
  note: string | null;
  created_at: string;
}

/** GET `/countries/:cc/crfs/:id/payments` — the ledger + the derived figures. */
export interface CrfLedgerDto {
  crf_id: string;
  currency: string;
  amount: string | null;
  received_amount: string;
  pending_amount: string | null;
  entries: PaymentEntryDto[];
}

export async function fetchCrfLedger(
  countryCode: string,
  crfId: string,
  signal?: AbortSignal,
): Promise<CrfLedgerDto> {
  return apiRequest<CrfLedgerDto>(`/countries/${countryCode}/crfs/${crfId}/payments`, { signal });
}

/**
 * The write. `source`/`archive_ref` are deliberately unrepresentable here — the
 * server authors provenance (`manual`), and only B3's backfill script writes
 * `legacy` rows. The form offers only what this body can say (spec §4-B4).
 */
export interface RecordPaymentEntryBody {
  kind: PaymentEntryDto['kind'];
  amount: string;
  currency: string;
  paid_on: string;
  method?: Exclude<PaymentEntryDto['method'], null>;
  invoice_ref?: string;
  note?: string;
}

export async function recordPaymentEntry(
  countryCode: string,
  crfId: string,
  body: RecordPaymentEntryBody,
): Promise<PaymentEntryDto> {
  return apiRequest<PaymentEntryDto>(`/countries/${countryCode}/crfs/${crfId}/payments`, {
    method: 'POST',
    body,
  });
}

export interface PutPointsBody {
  points: {
    description: string;
    confirmation: CrfPointDto['confirmation'];
    remark?: string | null;
  }[];
}

/** The grid, replaced WHOLE — the server numbers rows 1..N from array order. */
export async function putCrfPoints(crfId: string, body: PutPointsBody): Promise<CrfDto> {
  return apiRequest<CrfDto>(`/crfs/${crfId}/points`, { method: 'PUT', body });
}

export async function confirmCrf(crfId: string): Promise<CrfDto> {
  return apiRequest<CrfDto>(`/crfs/${crfId}/confirm`, { method: 'POST', body: {} });
}

/**
 * Record the confirmation as SENT (first call) or a reminder (later calls).
 * ⚠ STATE ONLY — no customer email exists in this system (OQ-A6, owner-ruled).
 * The screens that offer this action say so; a toast claiming "mail sent" would
 * be the UI lying about a transport that is not there.
 */
export async function sendCrfConfirmation(crfId: string): Promise<CrfDto> {
  return apiRequest<CrfDto>(`/crfs/${crfId}/send-confirmation`, { method: 'POST', body: {} });
}

/* ───────────────────────────────────────────────────────── maintenance ── */

/**
 * ONE RECORDED YEAR OF COVER — `MaintenanceTermView`, maintenance.service.ts (migration 40).
 *
 * ⚠ `amount` IS A REGISTERED MONEY NAME, so it arrives as the figure OR as `'•••'`, and the
 * panel renders it through the masked-cell idiom exactly as `crf.amount` is. It was called
 * `amount` on purpose — a new money field name would have needed a `MONEY_FIELDS` edit, and
 * the registry's own header warns what a forgotten one leaks.
 *
 * ⚠ AND IT IS NULLABLE, which is a third state and not a synonym for either of the other
 * two: a term may be recorded before anybody has priced the year.
 */
export interface MaintenanceTermDto {
  id: string;
  crf_id: string;
  country_id: number;
  /** 'YYYY-MM-DD', inclusive. */
  term_start: string;
  /** 'YYYY-MM-DD', inclusive — the last day covered. */
  expiry_date: string;
  /** Money — the figure or '•••', the server's call. Never parse it. */
  amount: string | null;
  currency: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * What the panel PROPOSES for the next term — never stored, never authoritative.
 *
 * ⚠ `basis` IS WHY THIS TRAVELS AT ALL. A prefilled date with no stated origin reads as an
 * oracle; `'delivery'` means "one year from the last deployment" and `'renewal'` means "the
 * day after the term already recorded". An operator who knows which is looking at can
 * correct it, which is the whole point of "prefilled AND editable".
 */
export interface MaintenancePrefillDto {
  term_start: string | null;
  expiry_date: string | null;
  basis: 'delivery' | 'renewal' | null;
}

/** `MaintenancePanelView` — the whole panel in one read. */
export interface MaintenancePanelDto {
  crf_id: string;
  doc_no: string;
  country_id: number;
  terms: MaintenanceTermDto[];
  prefill: MaintenancePrefillDto;
  /** The delivery the prefill was read from, in the COUNTRY's calendar. Null = nothing deployed. */
  delivered_on: string | null;
  /**
   * ⚠ THE OPTION-D DISCLOSURE, AND IT IS THE SERVER'S SENTENCE, NOT THIS CLIENT'S.
   *
   * A recorded maintenance date that reminds nobody is the failure the whole renewals
   * coupling exists to prevent, and it is invisible from the screen: the date looks
   * identical either way. So the server derives whether the reminder engine can actually
   * see this term and says so in words, and `null` the day it can. The panel PRINTS it and
   * composes nothing of its own — a client-side guess at this would be a claim about a
   * nightly job it cannot observe.
   */
  reminder_notice: string | null;
}

/**
 * ⚠ REFUSES ON A CRF, INCLUDING ON THE READ (`MaintenanceService.requireProject`), and that
 * is why the panel is mounted only for `work_type === 'project'`. An empty panel on a CRF
 * would invite the screen to print "Maintenance: none" on a record where the question does
 * not arise.
 */
export async function fetchMaintenancePanel(
  crfId: string,
  signal?: AbortSignal,
): Promise<MaintenancePanelDto> {
  return apiRequest<MaintenancePanelDto>(`/crfs/${crfId}/maintenance`, { signal });
}

export interface RecordMaintenanceBody {
  term_start: string;
  /** ABSENT means the standard year, computed by the SERVER — not by this client. */
  expiry_date?: string;
  amount?: string | null;
  currency?: string | null;
  note?: string | null;
}

export async function recordMaintenanceTerm(
  crfId: string,
  body: RecordMaintenanceBody,
): Promise<MaintenanceTermDto> {
  return apiRequest<MaintenanceTermDto>(`/crfs/${crfId}/maintenance`, { method: 'POST', body });
}

/**
 * The other half of "prefilled and editable".
 *
 * ⚠ AN ABSENT `expiry_date` HERE MEANS "LEAVE IT", NOT "RECOMPUTE IT" — the opposite of the
 * create path, and the server's DTO says so in writing. Moving the start and silently
 * sliding the end would overwrite a date somebody negotiated.
 */
export async function updateMaintenanceTerm(
  crfId: string,
  termId: string,
  body: Partial<RecordMaintenanceBody>,
): Promise<MaintenanceTermDto> {
  return apiRequest<MaintenanceTermDto>(`/crfs/${crfId}/maintenance/${termId}`, {
    method: 'PATCH',
    body,
  });
}

/* ─────────────────────────────────────────────────────────────────── files ── */

export const CRF_FILE_MAX_BYTES = 10 * 1024 * 1024;
export const CRF_FILE_ACCEPT = '.pdf,.zip,.png,.jpg,.jpeg,.tdl';

export async function fetchCrfFiles(crfId: string, signal?: AbortSignal): Promise<CrfFileDto[]> {
  return apiRequest<CrfFileDto[]>(`/crfs/${crfId}/files`, { signal });
}

/**
 * One upload path, for scope documents AND for manuals.
 *
 * ⚠ THE TYPE RIDES THE QUERY STRING, NOT THE FORM. The API's multipart gate sets
 * `fields: 0` and that limit object is SHARED with the work-order upload
 * (`work-file-multipart.interceptor.ts:54-60`), so appending a second form part
 * here would be refused — and "fixing" that by raising the limit would loosen the
 * work-order surface's shape gate as a side effect. Omitting `docType` posts
 * exactly the request this function posted before manuals existed.
 */
export async function uploadCrfFile(
  crfId: string,
  file: File,
  docType?: ManualDocType,
): Promise<CrfFileDto> {
  const form = new FormData();
  form.append('file', file);
  const query = docType === undefined ? '' : `?doc_type=${encodeURIComponent(docType)}`;
  return apiRequest<CrfFileDto>(`/crfs/${crfId}/files${query}`, { method: 'POST', body: form });
}

/**
 * Bytes behind the JWT guard — a plain `<a href>` gets a 401 (no Authorization
 * header), so the download rides `fetchBinary`, the same helper the quote PDF
 * uses, and the caller turns the blob into an object URL.
 */
export async function downloadCrfFile(crfId: string, fileId: string): Promise<BinaryResponse> {
  return fetchBinary(`/crfs/${crfId}/files/${fileId}`);
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const crfKeys = {
  list: (cc: string, filters: CrfListFilters) => ['crfs', cc, 'list', filters] as const,
  // ⚠ THE FILTERS ARE IN THE KEY. They were not, because the counts took none; now that
  // they narrow the count, a cache keyed without them would serve the "All" numbers above
  // the "Projects" rows — the exact strip/list disagreement the API route refuses to make.
  counts: (cc: string, filters: CrfCountFilters) => ['crfs', cc, 'counts', filters] as const,
  vocabularyCounts: (cc: string, filters: CrfCountFilters) =>
    ['crfs', cc, 'vocabulary-counts', filters] as const,
  platformOptions: (cc: string, workType?: WorkType) =>
    ['crfs', cc, 'platform-options', workType ?? 'crf'] as const,
  stackOptions: (cc: string) => ['crfs', cc, 'stack-options'] as const,
  awaiting: (cc: string, page: number) => ['crfs', cc, 'awaiting', page] as const,
  record: (id: string) => ['crfs', 'record', id] as const,
  maintenance: (id: string) => ['crfs', 'maintenance', id] as const,
  files: (id: string) => ['crfs', 'files', id] as const,
};
